import { createSelector, createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";

const companiesAdapter = createEntityAdapter({});

const initialState = companiesAdapter.getInitialState();

export const companiesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query({
      query: () => ({
        url: "/companies",
        validateStatus: (response, result) => {
          return response.status === 200 && !result.isError && result.length;
        },
      }),
      transformResponse: (responseData) => {
        const loadedCompanies = responseData.map((company) => {
          company.id = company._id;
          return company;
        });
        return companiesAdapter.setAll(initialState, loadedCompanies);
      },
      providesTags: (result, error, arg) => {
        if (result?.ids) {
          return [
            { type: "Company", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Company", id })),
          ];
        } else return [{ type: "Company", id: "LIST" }];
      },
    }),
    getCompanyById: builder.query({
      query: (id) => ({
        url: `/companies/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => {
        return [{ type: "Company", id: arg }];
      },
    }),
    addCompany: builder.mutation({
      query: ({ body }) => ({
        url: "/companies",
        method: "POST",
        body,
        validateStatus: (response, result) => {
          return response.status === 201 && !result.isError;
        },
      }),
      invalidatesTags: [{ type: "Company", id: "LIST" }],
    }),

    updateCompany: builder.mutation({
      query: ({ body }) => ({
        url: "/companies",
        method: "PATCH",
        body,
      }),
      transformResponse: (responseData, meta) => {
        return { ...responseData, status: meta.response.status };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Company", id: arg.id },
      ],
    }),

    deleteCompany: builder.mutation({
      query: ({ id }) => ({
        url: `/companies`,
        method: "DELETE",
        body: { id },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Company", id: arg.id },
      ],
    }),
    getProcurementRequestsByCompany: builder.query({
      query: () => ({
        url: `/companies/procurment_req`,
        method: "GET",
      }),
    }),
    getCompanyDirectPurchaseReq: builder.query({
      query: () => ({
        url: `/companies/direct_req`,
        method: "GET",
      }),
    }),
    getCompanyOffers: builder.query({
      query: ({ page, per_page }) => ({
        url: `/companies/offers/?page=${page}&per_page=${per_page}`,
        method: "GET",
      }),
      providesTags: [{ type: "BidOffer", id: "LIST" }],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetCompanyByIdQuery,
  useAddCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetProcurementRequestsByCompanyQuery,
  useGetCompanyDirectPurchaseReqQuery,
  useGetCompanyOffersQuery,
} = companiesApiSlice;

// returns the query result object
export const selectCompaniesResult =
  companiesApiSlice.endpoints.getCompanies.select("companiesList");

// creates memoized selector with options
export const selectCompanies = (companyId) =>
  createSelector(selectCompaniesResult, (companiesResult) => {
    if (companyId) {
      let company = null;
      const { data } = companiesResult;

      for (const key in data?.entities) {
        if (data?.entities[key].id === companyId) {
          company = data?.entities[key];
          break;
        }
      }

      return {
        data: company,
        isLoading: companiesResult.isLoading,
        isSuccess: company !== null && companiesResult.isSuccess,
        isError:
          companiesResult.isError ||
          (company === null && companiesResult.isSuccess),
        error: companiesResult.error, // Include error from the query result object
      };
    } else {
      return {
        data: Object.values(companiesResult.data?.entities || {}), // normalized array with entities
        isLoading: companiesResult.isLoading,
        isSuccess: companiesResult.isSuccess,
        isError: companiesResult.isError,
        error: companiesResult.error, // Include error from the query result object
      };
    }
  });

// creates memoized selector
const selectCompaniesData = createSelector(
  selectCompaniesResult,
  (companiesResult) => companiesResult.data // normalized state object with ids & entities
);

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
  selectAll: selectAllCompanies,
  selectById: selectCompanyById,
  selectIds: selectCompanyIds,
  // Pass in a selector that returns the companies slice of state
} = companiesAdapter.getSelectors(
  (state) => selectCompaniesData(state) ?? initialState
);
